import { useState, useEffect, useCallback } from "react";
import {
  BookOpen, PenLine, Hash, ChevronDown, ChevronRight,
  CheckCircle, XCircle, RefreshCw, Loader2, Settings,
} from "lucide-react";
import { getFLNHealth } from "@/lib/api";

// ── Expected counts ────────────────────────────────────────────────────────────

const R_LEVELS  = ["R1","R2","R3","R4","R5","R6","R7","R8","R9","R10"];
const W_LEVELS  = ["W1","W2","W3","W4","W5","W6","W7","W8","W9","W10","W11","W12","W13"];
const C_LEVELS  = ["C1","C2","C3","C4","C5","C6","C7","C8","C9","C10","C11","C12","C13","C14","C15","C16","C17","C18"];

function buildExpected() {
  const out = { reading: {}, writing: {}, counting: {} };
  R_LEVELS.forEach(l => {
    out.reading[l] = l === "R1"
      ? { practice: 0, assessment: 0, noItems: true, noItemsLabel: "Alphabet recognition — no dataset items" }
      : { practice: 30, assessment: 10 };
  });
  W_LEVELS.forEach(l => {
    out.writing[l] = l === "W1"
      ? { practice: 0, assessment: 0, noItems: true, noItemsLabel: "Alphabet writing — no dataset items" }
      : { practice: 30, assessment: 10 };
  });
  C_LEVELS.forEach(l => {
    out.counting[l] = { practice: 50, assessment: 15 };
  });
  return out;
}

const EXPECTED = buildExpected();

const EXPECTED_TOTAL = {
  reading:  9 * 40,    // 360
  writing:  12 * 40,   // 480
  counting: 18 * 65,   // 1170
  total:    2010,
};

// ── Head config ────────────────────────────────────────────────────────────────

const HEAD_CONFIG = {
  reading:  { label: "Reading",  Icon: BookOpen, levels: R_LEVELS, accent: "#2563eb", accentBg: "#eff6ff" },
  writing:  { label: "Writing",  Icon: PenLine,  levels: W_LEVELS, accent: "#7c3aed", accentBg: "#f5f3ff" },
  counting: { label: "Counting", Icon: Hash,     levels: C_LEVELS, accent: "#0d9488", accentBg: "#f0fdfa" },
};

const HEADS = ["reading", "writing", "counting"];

// ── Small helpers ──────────────────────────────────────────────────────────────

function Chip({ ok, children }) {
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold tabular-nums"
      style={ok
        ? { background: "#d1fae5", color: "#065f46" }
        : { background: "#fee2e2", color: "#991b1b" }
      }
    >
      {ok ? <CheckCircle size={10} /> : <XCircle size={10} />}
      {children}
    </span>
  );
}

// ── Collapsible head section ───────────────────────────────────────────────────

function HeadSection({ head, healthData }) {
  const [open, setOpen] = useState(true);
  const { label, Icon, levels, accent, accentBg } = HEAD_CONFIG[head];
  const expected = EXPECTED[head];
  const actualHead = healthData?.[head] || {};

  let totalActual = 0;
  let allOk = true;

  const rows = levels.map(level => {
    const exp  = expected[level];
    const act  = actualHead[level] || { practice: 0, assessment: 0 };
    const practiceOk  = act.practice  === exp.practice;
    const assessOk    = act.assessment === exp.assessment;
    const levelOk     = exp.noItems ? (act.practice === 0 && act.assessment === 0) : (practiceOk && assessOk);
    if (!levelOk) allOk = false;
    totalActual += act.practice + act.assessment;
    return { level, exp, act, levelOk, practiceOk, assessOk };
  });

  const expTotal = EXPECTED_TOTAL[head];
  const totalOk  = totalActual === expTotal;

  return (
    <div
      className="rounded-xl border overflow-hidden"
      style={{ borderColor: allOk ? "#a7f3d0" : "#fca5a5" }}
      data-testid={`fln-health-section-${head}`}
    >
      {/* Section header */}
      <button
        className="w-full flex items-center justify-between px-5 py-4 transition-colors"
        style={{ background: accentBg }}
        onClick={() => setOpen(o => !o)}
        data-testid={`fln-health-toggle-${head}`}
      >
        <div className="flex items-center gap-3">
          <Icon size={18} style={{ color: accent }} />
          <span className="font-bold text-slate-800">{label}</span>
          {allOk
            ? <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: "#d1fae5", color: "#065f46" }}>All OK</span>
            : <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: "#fee2e2", color: "#991b1b" }}>Mismatch</span>
          }
        </div>
        <div className="flex items-center gap-3">
          <Chip ok={totalOk}>{totalActual} / {expTotal}</Chip>
          {open ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
        </div>
      </button>

      {/* Level rows */}
      {open && (
        <div className="divide-y divide-slate-100">
          {/* Table header */}
          <div className="grid grid-cols-4 gap-2 px-5 py-2 bg-slate-50 text-xs font-semibold text-slate-400 uppercase tracking-wider">
            <span>Level</span>
            <span className="text-center">Practice</span>
            <span className="text-center">Assessment</span>
            <span className="text-center">Status</span>
          </div>
          {rows.map(({ level, exp, act, levelOk, practiceOk, assessOk }) => (
            <div
              key={level}
              className="grid grid-cols-4 gap-2 px-5 py-2.5 items-center hover:bg-slate-50/60 transition-colors"
              data-testid={`fln-health-row-${head}-${level}`}
            >
              <span className="text-sm font-bold" style={{ color: accent }}>{level}</span>

              {exp.noItems ? (
                <span
                  className="col-span-2 text-xs text-slate-400 italic"
                  data-testid={`fln-health-no-items-${head}-${level}`}
                >
                  {exp.noItemsLabel}
                </span>
              ) : (
                <>
                  <div className="text-center">
                    <Chip ok={practiceOk}>{act.practice}</Chip>
                    <span className="text-xs text-slate-300 ml-1">/ {exp.practice}</span>
                  </div>
                  <div className="text-center">
                    <Chip ok={assessOk}>{act.assessment}</Chip>
                    <span className="text-xs text-slate-300 ml-1">/ {exp.assessment}</span>
                  </div>
                </>
              )}

              <div className="flex justify-center">
                {levelOk
                  ? <CheckCircle size={16} style={{ color: "#10b981" }} />
                  : <XCircle    size={16} style={{ color: "#ef4444" }} />
                }
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── V2 Toggle placeholder ──────────────────────────────────────────────────────

function V2Toggle({ label }) {
  return (
    <div className="flex items-center justify-between py-3 opacity-50 cursor-not-allowed">
      <div>
        <p className="text-sm font-medium text-slate-700">{label}</p>
        <p className="text-xs text-slate-400 mt-0.5">Coming in V2</p>
      </div>
      <div
        className="w-10 h-5 rounded-full bg-slate-200 relative flex-shrink-0"
        title="Coming in V2"
      >
        <div className="w-4 h-4 rounded-full bg-white shadow absolute top-0.5 left-0.5 transition-transform" />
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

export default function FLNHealth({ token }) {
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getFLNHealth(token);
      setHealthData(res.health);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to load FLN health data");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Compute grand total from live data
  const grandTotal = !healthData ? 0 : HEADS.reduce((sum, head) => {
    const headData = healthData[head] || {};
    return sum + Object.values(headData).reduce(
      (s, lv) => s + (lv.practice || 0) + (lv.assessment || 0),
      0
    );
  }, 0);
  const grandTotalOk = grandTotal === EXPECTED_TOTAL.total;

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-8">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">FLN Module</h1>
          <p className="text-sm text-slate-500 mt-1">Dataset health and feature settings</p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
          data-testid="fln-health-refresh-btn"
        >
          {loading
            ? <Loader2 size={14} className="animate-spin" />
            : <RefreshCw size={14} />
          }
          Refresh
        </button>
      </div>

      {/* ── FLN Dataset Health card ─────────────────────────────────────────── */}
      <section data-testid="fln-dataset-health-card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-bold text-slate-800">FLN Dataset Health</h2>
          {!loading && !error && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">Grand total:</span>
              <Chip ok={grandTotalOk} data-testid="fln-health-grand-total">
                {grandTotal} / {EXPECTED_TOTAL.total}
              </Chip>
            </div>
          )}
        </div>

        {loading && (
          <div className="flex items-center justify-center gap-3 py-16 rounded-xl border border-slate-200 bg-white">
            <Loader2 size={20} className="animate-spin text-slate-400" />
            <span className="text-sm text-slate-400">Loading FLN data…</span>
          </div>
        )}

        {error && !loading && (
          <div className="p-4 rounded-xl border border-red-200 bg-red-50 text-sm text-red-700">
            {error}
          </div>
        )}

        {!loading && !error && (
          <div className="space-y-3">
            {HEADS.map(head => (
              <HeadSection key={head} head={head} healthData={healthData} />
            ))}
          </div>
        )}
      </section>

      {/* ── FLN Settings (V2 placeholders) ─────────────────────────────────── */}
      <section
        className="rounded-xl border border-slate-200 bg-white overflow-hidden"
        data-testid="fln-settings-section"
      >
        <div className="flex items-center gap-2.5 px-5 py-4 border-b border-slate-100">
          <Settings size={16} className="text-slate-400" />
          <h2 className="text-base font-bold text-slate-800">FLN Settings</h2>
        </div>
        <div className="px-5 divide-y divide-slate-100">
          <V2Toggle label="Voice Mode" />
          <V2Toggle label="OCR Writing Evaluation" />
        </div>
      </section>
    </div>
  );
}
